import { Component, OnInit, ViewChild, inject, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OrdersService } from 'src/app/services/orders.service';
import { CancelledRqa, RqaReservationChangeDTO, RqaReservationDTO } from 'src/app/model/reservation.model';
import { Table, TableModule } from 'primeng/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Router, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateService } from 'src/app/services/date.service';
import { DATE_TIME_FORMAT } from 'src/app/shared/constants/date.constants';
import { UserConfigService } from 'src/app/services/user-config.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DialogManagerService } from 'src/app/services/dialog-manager.service';
import { SelectDateDialogComponent } from 'src/app/shared/components/select-date-dialog/select-date-dialog.component';
import { Route } from 'src/app/model/route.enum';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { LayoutUtilsService } from 'src/app/services/layout-utils.service';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { PopoverModule } from 'primeng/popover';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
@Component({
  selector: 'rqa-archival-orders',
  templateUrl: './archival-orders.component.html',
  styleUrls: ['./archival-orders.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    TranslateModule,
    DatePickerModule,
    PopoverModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    CheckboxModule
  ],
  providers: [RouterModule]
})
export class ArchivalOrdersComponent implements OnInit, AfterViewInit {
  @ViewChild('dt') pTable!: Table;
  @ViewChild('calendar') pCalendar!: DatePicker;
  private ordersService = inject(OrdersService);
  private dateService = inject(DateService);
  private userConfig = inject(UserConfigService);
  private dialogManager = inject(DialogManagerService);
  private dialogService = inject(DialogService);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private layoutUtilsService = inject(LayoutUtilsService);
  orders: RqaReservationDTO[];
  dateRange: Date[];
  dateFormat: string = 'yy-mm-dd 06:00';
  selectedOrders: RqaReservationChangeDTO[] = [];
  ref: DynamicDialogRef;
  maxDate: Date = moment().add(1, 'days').toDate();
  minDate: Date = moment().subtract(30, 'days').toDate();
  tableHeight: string = '400px';
  onlyMyOrders: boolean = false;

  ngOnInit(): void {
    this.getArchivalOrders(this.onlyMyOrders);
    this.setFilterDateFormat();
  }

  ngAfterViewInit(): void {
    this.getTableHeight();
  }

  filterByDate(): void {
    if (this.dateRange.length && this.dateRange[1] !== null) {
      const startDate = moment(this.dateRange[0]).set({ hour: 6, minute: 0 }).format(DATE_TIME_FORMAT);
      let endDate = moment(this.dateRange[1]).set({ hour: 6, minute: 0 }).format(DATE_TIME_FORMAT);
      if (startDate === endDate) {
        this.dateRange[1] = new Date(moment(startDate).add(1, 'days').format(DATE_TIME_FORMAT));
        endDate = moment(this.dateRange[1]).set({ hour: 6, minute: 0 }).format(DATE_TIME_FORMAT);
      }
      this.pTable.filter(this.dateService.convertDateToUserTimezone(startDate), 'startDate', 'dateSameAfter');
      this.pTable.filter(this.dateService.convertDateToUserTimezone(endDate), 'endDate', 'dateSameBefore');
      this.pCalendar.overlayVisible = false;
    }
  }

  clearFilters(): void {
    this.dateRange = [];
    this.pTable.filters = {};
    this.pTable._filter();
  }

  onDateSelect(): void {
    if (
      this.dateRange &&
      this.dateRange[0] &&
      this.dateRange[1] &&
      this.dateRange[0].getTime() === this.dateRange[1].getTime()
    ) {
      this.dateRange = [this.dateRange[0]];
    }
  }

  showOrderHistory(order: CancelledRqa): void {
    this.getReservationHistory(order.uuid);
  }

  filterReservations($event: Event, matchType: string): void {
    this.pTable.filterGlobal(($event.target as HTMLInputElement).value, matchType);
  }

  onLoadSelectedOrders(): void {
    this.ref = this.dialogService.open(SelectDateDialogComponent, {
      header: this.translateService.instant('dialogs.loadOrder.header'),
      contentStyle: { overflow: 'auto' },
      data: {
        label: this.translateService.instant('dialogs.loadOrder.orderDate')
      },
      width: '500px',
      baseZIndex: 10000
    });
    this.dialogManager.addDialog(this.ref);
    this.ref.onClose.subscribe((date) => {
      if (date) {
        this.loadArchivalOrder(this.selectedOrders, date);
      }
      this.dialogManager.removeDialog(this.ref);
    });
  }

  private getReservationHistory(orderUuid: string): void {
    const orderIdx = this.orders.findIndex((order) => order.uuid === orderUuid);
    if (this.orders[orderIdx].reservationHistory?.length) return;
    this.ordersService.getReservationHistory(orderUuid).subscribe((data) => {
      this.orders[orderIdx].reservationHistory = data;
    });
  }

  private loadArchivalOrder(orders: RqaReservationChangeDTO[], date: string): void {
    const payload = orders.map((order) => ({
      ...order,
      startDate: this.dateService.convertStartOrderDateToRqaFormat(order.startDate, date),
      endDate: this.dateService.convertEndOrderDateToRqaFormat(order.startDate, order.endDate, date)
    }));
    this.ordersService.editSentOrders(payload).subscribe({
      next: () => {
        this.router.navigate([Route.HOME]);
      },
      error: (err) => {
        console.log(err);
        this.messageService.add({
          severity: 'error',
          detail: err.error.message,
          life: 3000
        });
      }
    });
  }

  private getArchivalOrders(onlyMyOrders: boolean): void {
    this.ordersService.getArchivalReservations(onlyMyOrders).subscribe((data) => {
      this.orders = data.map((el) => {
        el.startDate = this.dateService.convertDateToUserTimezone(el.startDate);
        el.endDate = this.dateService.convertDateToUserTimezone(el.endDate);
        return el;
      });
    });
  }

  private setFilterDateFormat(): void {
    const timeZone = this.userConfig.getTimezone();
    const hours = '06:00';
    const localHours = this.dateService.adjustTimeToLocal(hours);
    if (timeZone !== 'UTC') {
      this.dateFormat = 'yy-mm-dd ' + localHours;
    } else {
      this.dateFormat = 'yy-mm-dd ' + hours;
    }
  }

  private getTableHeight(): void {
    setTimeout(() => {
      this.tableHeight = this.layoutUtilsService.getTableHeightForOrders();
    }, 1000);
  }
}
